import React, { useState, useEffect } from "react";

import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";

import { getNotes, createNote, updateNote, deleteNote, Note } from "sj-ui-toolkit/src/api/notes";
import { QuillEditor } from "sj-ui-toolkit/src/components/QuillEditor/QuillEditor";

// Local dependencies
import S from "./_styles.module.scss";

// Notes component
const Notes = ({ lessonId, levelId = undefined }: { lessonId?: number; levelId?: number }) => {
  // State for notes
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);

  // The notes API takes string ids; the ids we receive are numeric.
  const lessonIdStr = lessonId != null ? String(lessonId) : "";
  const levelIdStr = levelId != null ? String(levelId) : undefined;

  // Load notes on component mount
  useEffect(() => {
    const loadNotes = async () => {
      if (lessonId) {
        const userNotes = await getNotes({ lessonId: lessonIdStr, levelId: levelIdStr });
        setNotes(userNotes || []);
      }
    };
    loadNotes();
  }, [lessonId, levelId]);

  const handleAddUpdateNote = async () => {
    const trimmedText = noteText.trim();
    if (!trimmedText) return;

    if (editingNoteId) {
      // Update existing note
      const updatedNote = await updateNote({
        lessonId: lessonIdStr,
        noteId: String(editingNoteId),
        noteText: trimmedText,
        levelId: levelIdStr,
      });
      const newNotes = notes.map(note => (note.id === editingNoteId ? updatedNote : note));
      setNotes(newNotes);
      setEditingNoteId(null);
    } else {
      // Create new note
      const userNote = await createNote({
        lessonId: lessonIdStr,
        noteText: trimmedText,
        levelId: levelIdStr,
      });
      setNotes([userNote, ...notes]);
    }
    setNoteText("");
  };

  const handleDeleteNote = async (id: number) => {
    await deleteNote({ lessonId: lessonIdStr, levelId: levelIdStr, noteId: String(id) });
    setNotes(notes.filter(note => note.id !== id));
  };

  const handleEditNote = (id: number) => {
    setEditingNoteId(id);
    setNoteText(notes.find(note => note.id === id)?.text ?? "");
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setNoteText("");
  };

  const handleEditorChange = (value: string) => {
    setNoteText(value);
  };

  return (
    <div className="notes-container py-2 px-4" data-testid="lesson-notes">
      {/* Note create/edit form */}
      <div className="flex flex-col mb-4">
        {/* <QuillHeader /> */}
        <QuillEditor value={noteText} onChange={handleEditorChange} />

        <div className="flex mt-2 gap-1">
          {editingNoteId && (
            <Button
              type="button"
              label="Cancel Edit"
              className="flex-1"
              text
              data-testid="lesson-notes-cancel-button"
              onClick={handleCancelEdit}
            />
          )}
          <Button
            type="button"
            label={editingNoteId ? "Update Note" : "Add Note"}
            className="flex-1"
            data-testid="lesson-notes-submit-button"
            onClick={handleAddUpdateNote}
          />
        </div>
      </div>

      {/* Notes list */}
      <div className="notes-list flex flex-col gap-3">
        {notes.length === 0 ? (
          <p className="no-notes">No notes yet. Add your first note above!</p>
        ) : (
          notes.map((note, index) => (
            <React.Fragment key={`note-fragment-${note.id}`}>
              {index > 0 && <Divider key={`divider-${note.id}`} />}
              <div id={`note-${note.id}`} key={note.id} data-testid={`lesson-note-${note.id}`}>
                <div className="note-header flex items-center">
                  <span className="flex-1 text-muted">{note.timestamp}</span>
                  <Button
                    label="Edit"
                    text
                    size="small"
                    data-testid={`lesson-note-${note.id}-edit-button`}
                    onClick={() => handleEditNote(note.id)}
                  />
                  <Button
                    label="Delete"
                    text
                    size="small"
                    data-testid={`lesson-note-${note.id}-delete-button`}
                    onClick={() => handleDeleteNote(note.id)}
                  />
                </div>
                <div className={S.noteDisplay}>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(note.text, { USE_PROFILES: { html: true } }),
                    }}
                  ></div>
                </div>
              </div>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default Notes;
